import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

// Import locale files
import ptBR from '@/locales/pt-BR.json';
import enUS from '@/locales/en-US.json';
import esES from '@/locales/es-ES.json';

export type Language = 'pt-BR' | 'en-US' | 'es-ES';

type LocaleData = typeof ptBR;

const locales: Record<Language, LocaleData> = {
  'pt-BR': ptBR,
  'en-US': enUS,
  'es-ES': esES,
};

const languageNames: Record<Language, string> = {
  'pt-BR': 'Português',
  'en-US': 'English',
  'es-ES': 'Español',
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
  availableLanguages: { code: Language; name: string }[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const detectBrowserLanguage = (): Language => {
  const browserLang = navigator.language || (navigator as any).userLanguage;
  
  if (browserLang.startsWith('en')) return 'en-US';
  if (browserLang.startsWith('es')) return 'es-ES';
  return 'pt-BR'; // Default fallback
};

const getStoredLanguage = (): Language | null => {
  try {
    const stored = localStorage.getItem('sentinela-language');
    if (stored && (stored === 'pt-BR' || stored === 'en-US' || stored === 'es-ES')) {
      return stored as Language;
    }
  } catch {
    // localStorage not available
  }
  return null;
};

const storeLanguage = (lang: Language): void => {
  try {
    localStorage.setItem('sentinela-language', lang);
  } catch {
    // localStorage not available
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [language, setLanguageState] = useState<Language>(() => {
    // Priority: stored > browser detection > default
    return getStoredLanguage() || detectBrowserLanguage();
  });

  // Sync with database when user is logged in
  useEffect(() => {
    if (!user) return;

    const fetchUserLanguage = async () => {
      const { data } = await supabase
        .from('user_language_preferences')
        .select('language_code')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data?.language_code) {
        setLanguageState(data.language_code as Language);
        storeLanguage(data.language_code as Language);
      }
    };

    fetchUserLanguage();
  }, [user]);

  const setLanguage = useCallback(async (lang: Language) => {
    setLanguageState(lang);
    storeLanguage(lang);

    // Persist to database if user is logged in
    if (user) {
      await supabase
        .from('user_language_preferences')
        .upsert({
          user_id: user.id,
          language_code: lang,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        });
    }
  }, [user]);

  const t = useCallback((key: string, params?: Record<string, string>): string => {
    const keys = key.split('.');
    let value: any = locales[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to pt-BR
        value = locales['pt-BR'];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if not found
          }
        }
        break;
      }
    }

    if (typeof value !== 'string') return key;

    // Replace parameters
    if (params) {
      return Object.entries(params).reduce(
        (str, [paramKey, paramValue]) => str.replace(`{${paramKey}}`, paramValue),
        value
      );
    }

    return value;
  }, [language]);

  const availableLanguages = Object.entries(languageNames).map(([code, name]) => ({
    code: code as Language,
    name,
  }));

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, availableLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export { LanguageContext };
